import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "../components/app/Layout";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import api from "../lib/api";
import AuthImage from "../components/app/AuthImage";
import EmptyState from "../components/app/EmptyState";
import { ArrowLeft, User, MapPin, Phone, Mail, Calendar as CalIcon, MessageSquare, Trophy } from "lucide-react";

/**
 * PlayerProfile — view for coaches/academies/parents to inspect a
 * self-registered player user. Read-only. Coaches can see bookings
 * between themselves and this player + start a chat.
 */
export default function PlayerProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { lang } = useI18n();
  const [player, setPlayer] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/players/${id}`);
        setPlayer(data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Player not found");
      }
      setLoading(false);
    })();
  }, [id]);

  const openChat = async () => {
    try {
      await api.post("/chat/threads", { other_user_id: id });
    } catch (_) {}
    nav("/chat");
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[50vh] flex items-center justify-center" data-testid="player-loading">
          <div className="pitch-loader"><div className="ball" /></div>
        </div>
      </Layout>
    );
  }

  if (err || !player) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto">
          <EmptyState
            icon={User}
            title={lang === "bg" ? "Играчът не е намерен" : "Player not found"}
            description={err || (lang === "bg" ? "Този профил вече не съществува." : "This profile no longer exists.")}
            cta={lang === "bg" ? "Назад към играчите" : "Back to players"}
            ctaTo="/children"
            testId="player-not-found"
          />
        </div>
      </Layout>
    );
  }

  const bookings = player.bookings || [];
  const upcoming = bookings.filter((b) => new Date(b.slot_at) > new Date() && ["pending", "paid", "confirmed"].includes(b.status));
  const history = bookings.filter((b) => !upcoming.find((u) => u.id === b.id));

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6 otp-page-enter" data-testid="player-profile">
        <Link to="/children" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-white/55 hover:text-pitch transition-colors" data-testid="back-to-players">
          <ArrowLeft className="w-3.5 h-3.5" /> {lang === "bg" ? "Към играчите" : "Back to players"}
        </Link>

        {/* Hero */}
        <div className="card-sharp p-6 sm:p-8 relative overflow-hidden rounded-lg">
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ background: "radial-gradient(ellipse at top left, #3B82F6, transparent 65%)" }} />
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5 relative">
            <div className="w-24 h-24 rounded-full flex-shrink-0 relative">
              {player.photo_url ? (
                <AuthImage
                  src={player.photo_url}
                  className="w-full h-full object-cover rounded-full border-2 border-pitch/50"
                  fallback={<div className="w-full h-full rounded-full bg-pitch text-white font-oswald flex items-center justify-center text-3xl">{player.name?.[0]}</div>}
                />
              ) : (
                <div className="w-full h-full rounded-full bg-pitch text-white font-oswald flex items-center justify-center text-3xl">
                  {player.name?.[0]}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="label-mono mb-1">// {lang === "bg" ? "Профил на играч" : "Player profile"}</div>
              <h1 className="font-oswald uppercase text-3xl sm:text-4xl tracking-tight leading-none mb-2">{player.name}</h1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-white/65">
                {player.age != null && <span className="chip-badge" data-testid="player-age">{player.age} {lang === "bg" ? "г." : "yr"}</span>}
                {player.position && <span className="chip-badge gold" data-testid="player-position">{player.position}</span>}
                {player.city && (
                  <span className="flex items-center gap-1.5" data-testid="player-city">
                    <MapPin className="w-3.5 h-3.5 text-pitch" /> {player.city}
                  </span>
                )}
              </div>
            </div>
            {user?.role === "coach" && user?.id !== player.id && (
              <button
                onClick={openChat}
                className="btn-primary px-5 py-2.5 flex items-center gap-2 self-start sm:self-center"
                data-testid="player-chat-btn"
              >
                <MessageSquare className="w-4 h-4" /> {lang === "bg" ? "Чат" : "Chat"}
              </button>
            )}
          </div>
        </div>

        {/* Contact */}
        <div className="card-sharp p-6 rounded-lg">
          <div className="label-mono mb-3">// {lang === "bg" ? "Контакт" : "Contact"}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {player.email && (
              <div className="flex items-center gap-2.5 text-sm" data-testid="player-email">
                <Mail className="w-4 h-4 text-pitch flex-shrink-0" />
                <a href={`mailto:${player.email}`} className="text-white/85 hover:text-pitch transition-colors truncate">{player.email}</a>
              </div>
            )}
            {player.phone ? (
              <div className="flex items-center gap-2.5 text-sm" data-testid="player-phone">
                <Phone className="w-4 h-4 text-pitch flex-shrink-0" />
                <a href={`tel:${player.phone}`} className="text-white/85 hover:text-pitch transition-colors">{player.phone}</a>
              </div>
            ) : player.has_phone ? (
              <div className="flex items-center gap-2.5 text-xs text-white/45 font-mono">
                <Phone className="w-4 h-4 flex-shrink-0" /> {lang === "bg" ? "Скрит номер" : "Hidden number"}
              </div>
            ) : null}
            {!player.email && !player.phone && !player.has_phone && (
              <span className="text-xs text-white/40 font-mono col-span-2">{lang === "bg" ? "Няма споделени контакти" : "No contact info shared"}</span>
            )}
          </div>
        </div>

        {/* Bookings */}
        {(user?.role === "coach" || user?.role === "academy") && (
          <div className="card-sharp p-6 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-mono mb-1">// {lang === "bg" ? "Сесии" : "Sessions"}</div>
                <h2 className="font-oswald uppercase text-2xl tracking-tight">
                  {lang === "bg" ? "Резервации с този играч" : "Bookings with this player"}
                </h2>
              </div>
              <span className="chip-badge" data-testid="player-bookings-count">{bookings.length}</span>
            </div>

            {bookings.length === 0 ? (
              <EmptyState
                icon={CalIcon}
                title={lang === "bg" ? "Все още няма резервации" : "No bookings yet"}
                description={lang === "bg" ? "Този играч още не е резервирал сесия при теб." : "This player hasn't booked a session with you yet."}
                compact
                testId="empty-player-bookings"
              />
            ) : (
              <div className="space-y-2">
                {upcoming.length > 0 && (
                  <>
                    <div className="label-mono">// {lang === "bg" ? "Предстоящи" : "Upcoming"}</div>
                    {upcoming.map((b) => <BookingRow key={b.id} b={b} lang={lang} />)}
                  </>
                )}
                {history.length > 0 && (
                  <>
                    <div className="label-mono pt-4">// {lang === "bg" ? "История" : "History"}</div>
                    {history.map((b) => <BookingRow key={b.id} b={b} lang={lang} />)}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Meta */}
        <div className="card-sharp p-6 rounded-lg">
          <div className="label-mono mb-3">// {lang === "bg" ? "Профил" : "Profile"}</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <Item label={lang === "bg" ? "Възраст" : "Age"} value={player.age != null ? `${player.age}` : "—"} />
            <Item label={lang === "bg" ? "Позиция" : "Position"} value={player.position || "—"} />
            <Item label={lang === "bg" ? "Град" : "City"} value={player.city || "—"} />
            <Item
              label={lang === "bg" ? "Регистриран" : "Registered"}
              value={player.created_at ? new Date(player.created_at).toLocaleDateString(lang === "bg" ? "bg-BG" : "en-US") : "—"}
            />
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Item({ label, value }) {
  return (
    <div className="border border-white/8 p-3 rounded-md hover:border-pitch/30 transition-colors">
      <div className="label-mono mb-1.5 text-[9px]">{label}</div>
      <div className="font-oswald uppercase tracking-tight text-base truncate">{value}</div>
    </div>
  );
}

function BookingRow({ b, lang }) {
  const statusCls = {
    pending: "amber",
    paid: "",
    confirmed: "",
    completed: "platinum",
    rejected: "",
    cancelled: "",
  }[b.status] || "";
  return (
    <div className="border border-white/8 hover:border-pitch/30 rounded-md p-4 flex items-center gap-4 transition-colors" data-testid={`booking-row-${b.id}`}>
      <div className="w-10 h-10 rounded-full bg-pitch/10 border border-pitch/30 flex items-center justify-center flex-shrink-0">
        <Trophy className="w-4 h-4 text-pitch" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-oswald uppercase tracking-tight text-sm">
          {new Date(b.slot_at).toLocaleString(lang === "bg" ? "bg-BG" : "en-US", {
            day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
          })}
        </div>
        <div className="text-xs text-white/45 font-mono">
          {b.duration_min || 60} {lang === "bg" ? "мин" : "min"}{b.amount != null && ` · €${b.amount}`}
        </div>
      </div>
      <span className={`chip-badge ${statusCls}`} data-testid={`booking-status-${b.id}`}>{b.status}</span>
    </div>
  );
}
